import { postJson } from "@/domain/common/client/ApiClient"
import {
  bracketMatchStatusLabel,
  bracketSizeLabel,
} from "@/domain/bracket/model/Bracket"
import {
  divisionLabel,
  genderLabel,
  sportLabel,
  weightClassLabel,
} from "@/domain/fight/model/Fight"
import { BracketMatchStatusPayload } from "@bracket/idl/main/bracket/model/BracketPt"
import {
  BracketGetAllByTournamentRequestPayload,
  BracketGetAllByTournamentResponsePayload,
  BracketGetByIdRequestPayload,
  BracketGetByIdResponsePayload,
  BracketGetByTournamentRequestPayload,
  BracketGetByTournamentResponsePayload,
  BracketGetMatchOrderRequestPayload,
  BracketGetMatchOrderResponsePayload,
} from "@bracket/idl/main/bracket/payload/BracketPayloadPt"
import { ageDivisionPayloadToJSON } from "@bracket/idl/main/fight/model/enums/AgeDivisionPt"
import { fightSportPayloadToJSON } from "@bracket/idl/main/fight/model/enums/FightSportPt"
import { genderPayloadToJSON } from "@bracket/idl/main/fight/model/enums/GenderPt"
import type {
  BracketMatchPayload,
  BracketMatchPlayerPayload,
  BracketPayload,
  BracketSummaryPayload,
} from "@bracket/idl/main/bracket/model/BracketPt"
import type { BracketMatchOrderPayload } from "@bracket/idl/main/bracket/payload/BracketPayloadPt"
import type {
  BracketDetail,
  BracketDrawRequestInput,
  BracketMatchItem,
  BracketMatchOrderItem,
  BracketMatchPlayerItem,
  BracketRound,
  BracketSummaryItem,
} from "@/domain/bracket/model/Bracket"

function toPlayerItem(
  payload: BracketMatchPlayerPayload | undefined,
): BracketMatchPlayerItem {
  return {
    playerId: payload?.playerId ?? "",
    name: payload?.playerName ?? "",
    gymName: payload?.gymName ?? "",
    weightKg: payload?.weightKg ?? "",
  }
}

function toMatchItem(payload: BracketMatchPayload): BracketMatchItem {
  return {
    id: payload.id,
    roundNo: payload.roundNo,
    matchNo: payload.matchNo,
    orderNo: payload.orderNo,
    roundName: payload.roundName,
    homePlayer: toPlayerItem(payload.homePlayer),
    awayPlayer: toPlayerItem(payload.awayPlayer),
    status: payload.status,
    statusLabel: bracketMatchStatusLabel(payload.status),
    isBye: payload.status === BracketMatchStatusPayload.BRACKET_MATCH_BYE,
  }
}

/** 라운드별로 접어 트리 렌더에 맞춘다 */
function toRounds(matches: BracketMatchItem[]): BracketRound[] {
  const roundNumbers = [...new Set(matches.map((match) => match.roundNo))].sort(
    (left, right) => left - right,
  )

  return roundNumbers.map((roundNo) => {
    const roundMatches = matches
      .filter((match) => match.roundNo === roundNo)
      .sort((left, right) => left.matchNo - right.matchNo)

    return {
      roundNo,
      roundName: roundMatches[0]?.roundName ?? `${roundNo}회전`,
      matches: roundMatches,
    }
  })
}

function toDetail(payload: BracketPayload): BracketDetail {
  const matches = payload.match.map(toMatchItem)

  return {
    id: payload.id,
    tournamentId: payload.tournamentId,
    sportLabel: sportLabel(payload.sport),
    divisionLabel: divisionLabel(payload.division),
    genderLabel: genderLabel(payload.gender),
    weightClassName: weightClassLabel(payload.weightClass),
    sizeLabel: bracketSizeLabel(payload.slotCount),
    slotCount: payload.slotCount,
    roundCount: payload.roundCount,
    isPublic: payload.isPublic,
    rounds: toRounds(matches),
  }
}

function toSummaryItem(payload: BracketSummaryPayload): BracketSummaryItem {
  // 1라운드 슬롯 수는 라운드 수로 정해진다 (라운드 3개 = 8강)
  const slotCount = 2 ** payload.roundCount

  return {
    id: payload.id,
    tournamentId: payload.tournamentId,
    sportLabel: sportLabel(payload.sport),
    divisionLabel: divisionLabel(payload.division),
    genderLabel: genderLabel(payload.gender),
    weightClassName: weightClassLabel(payload.weightClass),
    sizeLabel: bracketSizeLabel(slotCount),
    playerCount: payload.playerCount,
    matchCount: payload.matchCount,
    roundCount: payload.roundCount,
    isPublic: payload.isPublic,
  }
}

function toMatchOrderItem(
  payload: BracketMatchOrderPayload,
): BracketMatchOrderItem {
  return {
    orderNo: payload.orderNo,
    matchId: payload.matchId,
    bracketId: payload.bracketId,
    sportLabel: sportLabel(payload.sport),
    divisionLabel: divisionLabel(payload.division),
    genderLabel: genderLabel(payload.gender),
    weightClassName: weightClassLabel(payload.weightClass),
    roundName: payload.roundName,
    homePlayer: toPlayerItem(payload.homePlayer),
    awayPlayer: toPlayerItem(payload.awayPlayer),
    statusLabel: bracketMatchStatusLabel(payload.status),
    isBye: payload.status === BracketMatchStatusPayload.BRACKET_MATCH_BYE,
  }
}

/** 대진표 요약에서 편성 요청 값을 되돌린다 (재편성 · 삭제에 쓴다) */
function toDrawRequestInput(
  payload: BracketSummaryPayload,
): BracketDrawRequestInput {
  return {
    tournamentId: payload.tournamentId,
    sport: fightSportPayloadToJSON(payload.sport),
    division: ageDivisionPayloadToJSON(payload.division),
    gender: genderPayloadToJSON(payload.gender),
    weightClassName: weightClassLabel(payload.weightClass),
  }
}

/**
 * 대진표 도메인 클라이언트 (공개).
 *
 * 경로는 fight-bracket-server 의 `BracketRouter` 와 1:1 대응한다.
 * **편성한 대진표가 곧 공개 대진표다** — 확정 단계가 없다.
 */
const BracketClient = {
  async getByTournament(tournamentId: string): Promise<BracketSummaryItem[]> {
    const request = BracketGetByTournamentRequestPayload.create({
      tournamentId,
    })

    const json = await postJson<unknown>(
      "/bracket/get-by-tournament",
      BracketGetByTournamentRequestPayload.toJSON(request),
    )
    const response = BracketGetByTournamentResponsePayload.fromJSON(json)

    return response.data.map(toSummaryItem)
  },

  /** 대회 전체 대진표 — 체급 오름차순, 경기 목록까지 한 번에 온다 */
  async getAllByTournament(tournamentId: string): Promise<BracketDetail[]> {
    const request = BracketGetAllByTournamentRequestPayload.create({
      tournamentId,
    })

    const json = await postJson<unknown>(
      "/bracket/get-all-by-tournament",
      BracketGetAllByTournamentRequestPayload.toJSON(request),
    )
    const response = BracketGetAllByTournamentResponsePayload.fromJSON(json)

    return response.data.map(toDetail)
  },

  async getById(id: string): Promise<BracketDetail> {
    const request = BracketGetByIdRequestPayload.create({ id })

    const json = await postJson<unknown>(
      "/bracket/get-by-id",
      BracketGetByIdRequestPayload.toJSON(request),
    )
    const response = BracketGetByIdResponsePayload.fromJSON(json)

    if (!response.data) throw new Error("대진표를 찾을 수 없습니다.")

    return toDetail(response.data)
  },

  /** 대회 전체 경기 순서 (경기 번호 오름차순) */
  async getMatchOrder(
    tournamentId: string,
  ): Promise<BracketMatchOrderItem[]> {
    const request = BracketGetMatchOrderRequestPayload.create({ tournamentId })

    const json = await postJson<unknown>(
      "/bracket/get-match-order",
      BracketGetMatchOrderRequestPayload.toJSON(request),
    )
    const response = BracketGetMatchOrderResponsePayload.fromJSON(json)

    return response.data.map(toMatchOrderItem)
  },
}

export {
  BracketClient,
  toDetail,
  toDrawRequestInput,
  toMatchItem,
  toMatchOrderItem,
  toSummaryItem,
}
